import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import {
  CONTEXT_MATERIAL_STAGE,
  CONTEXT_MATERIAL_STAGE_STATE,
  ContextMaterialStage,
} from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { knowledgeApi, KnowledgeDocumentWithStages } from '@/lib/api'
import i18n from '@/i18n'

/**
 * One failed ingest stage, resolved into user-facing copy plus the stage the
 * retry endpoint expects. `stalled` is not a stage of its own: nothing ever
 * ran, so the retry restarts extraction.
 */
export interface KnowledgeStageFailure {
  stage: ContextMaterialStage
  key: 'extraction' | 'index' | 'understanding' | 'stalled'
  title: string
  detail: string
  reason?: string | null
}

const FAILED = CONTEXT_MATERIAL_STAGE_STATE.FAILED

/**
 * Stages fail independently and mean very different things to the user, so a
 * document can surface more than one notice. Order is severity-first: a
 * document we could not read at all outranks a missing summary.
 */
export function knowledgeStageFailures(
  doc: KnowledgeDocumentWithStages,
): Array<KnowledgeStageFailure> {
  const failures: Array<KnowledgeStageFailure> = []

  if (doc.extractionState === FAILED) {
    failures.push({
      stage: CONTEXT_MATERIAL_STAGE.EXTRACTION,
      key: 'extraction',
      title: i18n.t('library.fail.extractionTitle', { ns: 'app' }),
      detail: i18n.t('library.fail.extractionDetail', { ns: 'app' }),
      reason: doc.extractionError,
    })
  } else if (doc.stalled) {
    failures.push({
      stage: CONTEXT_MATERIAL_STAGE.EXTRACTION,
      key: 'stalled',
      title: i18n.t('library.fail.stalledTitle', { ns: 'app' }),
      detail: i18n.t('library.fail.stalledDetail', { ns: 'app' }),
    })
  }

  if (doc.indexState === FAILED) {
    failures.push({
      stage: CONTEXT_MATERIAL_STAGE.INDEX,
      key: 'index',
      title: i18n.t('library.fail.indexTitle', { ns: 'app' }),
      detail: i18n.t('library.fail.indexDetail', { ns: 'app' }),
      reason: doc.indexError,
    })
  }

  if (doc.understandingState === FAILED) {
    failures.push({
      stage: CONTEXT_MATERIAL_STAGE.UNDERSTANDING,
      key: 'understanding',
      title: i18n.t('library.fail.understandingTitle', { ns: 'app' }),
      detail: i18n.t('library.fail.understandingDetail', { ns: 'app' }),
      reason: doc.understandingError,
    })
  }

  return failures
}

interface KnowledgeStageFailureNoticeProps {
  doc: KnowledgeDocumentWithStages
  /** `compact` for library cards and list rows, `detail` for the document page. */
  variant?: 'compact' | 'detail'
  className?: string
  /** Receives the refreshed document returned by the retry endpoint. */
  onDocumentUpdated?: (doc: KnowledgeDocumentWithStages) => void
}

export function KnowledgeStageFailureNotice({
  doc,
  variant = 'detail',
  className,
  onDocumentUpdated,
}: KnowledgeStageFailureNoticeProps) {
  const { t } = useTranslation('app')
  const [retryingStage, setRetryingStage] = useState<ContextMaterialStage | null>(
    null,
  )

  const failures = knowledgeStageFailures(doc)

  async function handleRetry(stage: ContextMaterialStage) {
    if (retryingStage) {
      return
    }
    setRetryingStage(stage)
    try {
      const updated = await knowledgeApi.retryDocumentStage(doc.token, stage)
      onDocumentUpdated?.(updated)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('library.retryFailed')
      toast.error(message)
    } finally {
      setRetryingStage(null)
    }
  }

  if (failures.length === 0) {
    return null
  }

  const isCompact = variant === 'compact'

  return (
    <div
      className={`flex flex-col gap-1.5 ${className ?? ''}`}
      data-testid={`knowledge-stage-failures-${doc.token}`}
    >
      {failures.map((failure) => (
        <div
          key={failure.key}
          data-testid={`knowledge-stage-failure-${failure.key}-${doc.token}`}
          className={
            isCompact
              ? 'flex items-center gap-2 rounded-lg border border-destructive/25 bg-destructive/10 px-2.5 py-1.5'
              : 'flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4'
          }
        >
          <AlertTriangle
            className={
              isCompact
                ? 'h-3.5 w-3.5 shrink-0 text-destructive'
                : 'mt-0.5 h-4 w-4 shrink-0 text-destructive'
            }
          />
          <div className="min-w-0 flex-1">
            <Text
              size={isCompact ? 'xs' : 'sm'}
              className="font-medium text-destructive"
            >
              {failure.title}
            </Text>
            {isCompact ? null : (
              <>
                <Text size="sm" color="muted" className="mt-1">
                  {failure.detail}
                </Text>
                {failure.reason ? (
                  <Text
                    size="xs"
                    color="muted"
                    className="mt-1 break-words"
                    data-testid={`knowledge-stage-reason-${failure.key}-${doc.token}`}
                  >
                    {failure.reason}
                  </Text>
                ) : null}
              </>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            shape="pill"
            className={isCompact ? 'h-7 shrink-0 px-2.5 text-xs' : 'shrink-0'}
            disabled={retryingStage !== null}
            data-testid={`knowledge-stage-retry-${failure.key}-${doc.token}`}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              void handleRetry(failure.stage)
            }}
          >
            <RotateCcw
              className={
                isCompact ? 'mr-1 h-3 w-3' : 'mr-1.5 h-3.5 w-3.5'
              }
            />
            {retryingStage === failure.stage ? t('library.retrying') : t('library.retry')}
          </Button>
        </div>
      ))}
    </div>
  )
}
